import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {CheckInterval, CheckStatsLocation, LatestDowntime, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {QueryError, ResponseStatus} from "@/lib/types/common.ts";

export enum IsItDownServiceSort {
  MOST_POPULAR = "MOST_POPULAR",
  MOST_RECENT_OUTAGE = "MOST_RECENT_OUTAGE",
  RECENTLY_ADDED = "RECENTLY_ADDED",
  OPERATIONAL = "OPERATIONAL",
  REPORTED_ISSUES = "REPORTED_ISSUES",
  OUTAGE_REPORTED = "OUTAGE_REPORTED",
  MAINTENANCE = "MAINTENANCE",
}

export const IsItDownServiceSortName: Record<IsItDownServiceSort, string> = {
  [IsItDownServiceSort.MOST_POPULAR]: "Most Popular",
  [IsItDownServiceSort.MOST_RECENT_OUTAGE]: "Most Recent Outage",
  [IsItDownServiceSort.RECENTLY_ADDED]: "Recently Added",
  [IsItDownServiceSort.OPERATIONAL]: "Operational",
  [IsItDownServiceSort.REPORTED_ISSUES]: "Reported Issues",
  [IsItDownServiceSort.OUTAGE_REPORTED]: "Outage Reported",
  [IsItDownServiceSort.MAINTENANCE]: "Maintenance",
};

export enum IsItDownServiceStatus {
  UP = "UP",
  DOWN = "DOWN",
  MAINTENANCE = "MAINTENANCE",
}

export const IsItDownServiceStatusName: Record<IsItDownServiceStatus, string> = {
  [IsItDownServiceStatus.UP]: "Up",
  [IsItDownServiceStatus.DOWN]: "Outage Reported",
  [IsItDownServiceStatus.MAINTENANCE]: "Maintenance",
};

export const IsItDownServiceStatusLabel: Record<IsItDownServiceStatus, string> = {
  [IsItDownServiceStatus.UP]: "Up",
  [IsItDownServiceStatus.DOWN]: "Down",
  [IsItDownServiceStatus.MAINTENANCE]: "Maintenance",
};

export const IsItDownServiceStatusPill: Record<IsItDownServiceStatus, PillStatus> = {
  [IsItDownServiceStatus.UP]: PillStatus.UP,
  [IsItDownServiceStatus.DOWN]: PillStatus.OUTAGE_REPORTED,
  [IsItDownServiceStatus.MAINTENANCE]: PillStatus.UP,
};

export const IsItDownServiceStatusPillHeader: Record<IsItDownServiceStatus, PillStatus> = {
  [IsItDownServiceStatus.UP]: PillStatus.OPERATIONAL,
  [IsItDownServiceStatus.DOWN]: PillStatus.OUTAGE_REPORTED,
  [IsItDownServiceStatus.MAINTENANCE]: PillStatus.OPERATIONAL,
};

export interface PublicProfilesDownCategory {
  id: number;
  name: string;
  count: number;
}

export interface PublicProfilesDownCategoryStats {
  total: number;
  items: PublicProfilesDownCategory[];
  timestamps: string[];
  status: ResponseStatus;
}

export enum IsItDownVariant {
  CHECKS = "CHECKS",
  CLOUDSTATUS = "CLOUDSTATUS",
  HYBRID = "HYBRID",
}

export interface PublicProfile {
  id: string;
  slug: string;
  name?: string;
  description?: string;
  twitter: string;
  status_page?: string;
  image?: string;
  service_status: IsItDownServiceStatus;
  service_id?: number;
  variant: IsItDownVariant;
  categories?: PublicProfileCategory[];
}

interface IsItDownServices {
  total: number;
  items: PublicProfile[];
}

export interface IsItDownServicesResult {
  isItDownServices: IsItDownServices;
}

export interface PublicCloudStatusGroups {
  publicCloudStatusGroups: IsItDownServices;
}

export interface PublicProfileStats {
  service_msp_interval: number | null;
  service_created_at: string | null;
  totals: {
    uptime_pct?: number;
    downtime_secs: number;
    outages: number;
  } | null;
  intervals: CheckInterval[] | null;
  available_locations: CheckStatsLocation[] | null;
  location_response_times: LocationResponseTimes[] | null;
  last_check_timestamp: string; // ISO date string
  latest_downtime: LatestDowntime | null;
}

export interface PublicProfilesGroupIncident {
  id: string;
  title: string;
  status: IsItDownServiceStatus;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
}

interface PublicProfilesIncidentHistory {
  total: number;
  items: PublicProfilesGroupIncident[];
}

export interface PublicProfilesComponent {
  id: string;
  name: string;
  status: IsItDownServiceStatus;
  status_started_at: string; // ISO date string
  status_updated_at: string; // ISO date string
}

interface PublicProfilesComponentStatus {
  total: number;
  items: PublicProfilesComponent[];
}

export interface IsItDownServiceResult {
  service: PublicProfile | QueryError;
  stats: PublicProfileStats | QueryError;
  categoryStats: PublicProfilesDownCategoryStats;
  incidents: PublicProfilesIncidentHistory;
  components: PublicProfilesComponentStatus;
  similarServices: PublicProfile[];
}

interface PublicProfilesIncident {
  id: string;
  incident_title: string;
  incident_start: string;
  incident_duration: number;
  group_logo: string;
  group_name: string;
}

interface PublicProfilesRecentOutages {
  total: number;
  items: PublicProfilesIncident[];
}

export interface PublicProfilesRecentOutagesResult {
  publicProfilesRecentOutages: PublicProfilesRecentOutages;
}

interface PublicProfilesSubscribeData {
  subscribed: boolean;
}

interface PublicProfilesSubscribeResult {
  status: ResponseStatus;
  data: PublicProfilesSubscribeData;
}

export interface PublicProfilesSubscribeResponse {
  publicProfilesSubscribe: PublicProfilesSubscribeResult | QueryError;
}

interface PublicProfilesDownReportData {
  created: boolean;
}

interface PublicProfilesDownReportResult {
  status: ResponseStatus;
  data: PublicProfilesDownReportData;
}

export interface PublicProfilesDownReportResponse {
  publicProfilesDownReport: PublicProfilesDownReportResult | QueryError;
}

export interface PublicProfileCategory {
  id: string;
  name: string;
  weight: number;
}
